import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, Input, NgModule, OnInit, ViewChild } from '@angular/core';
import { BreakpointsService } from '@core/services/breakpoints/breakpoints.service';
import { CejOrientationSolutionsModule } from '@domains/cej-v2/cej-orientation-solutions/cej-orientation-solutions.component';
import { CejMultipleChoicesQuestionModule } from '@domains/cej-v2/cej-v2-form/cej-multiple-choices-question/cej-multiple-choices-question.component';
import { Answer, GRAPH, Question, QuestionValues } from '@domains/cej-v2/cej-v2-form/cej-orientation-graph';
import {
  CejCardResultConfig,
  CejCardResultLinkType,
  CejResultCardModule,
} from '@domains/cej-v2/cej-v2-form/cej-result-card/cej-result-card.component';
import { CejSimpleChoiceQuestionModule } from '@domains/cej-v2/cej-v2-form/cej-simple-choice-question/cej-simple-choice-question.component';
import { CejV2FormContactModalModule } from '@domains/cej-v2/cej-v2-form/cej-v2-form-contact-modal/cej-v2-form-contact-modal.component';
import { ScrollToService } from '@nicky-lenaers/ngx-scroll-to';
import { trackEventWithAtInternet } from '@wizbii/tracking';
import { fadeInOnEnterAnimation, fadeOutOnLeaveAnimation } from 'angular-animations';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { delay, filter, map, take, withLatestFrom } from 'rxjs/operators';

@Component({
  selector: 'app-cej-orientation-form',
  templateUrl: './cej-orientation-form.component.html',
  styleUrls: ['./cej-orientation-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [fadeInOnEnterAnimation({ duration: 600 }), fadeOutOnLeaveAnimation({ duration: 600 })],
})
export class CejOrientationFormComponent implements OnInit {
  @ViewChild('formDescription') formDescription!: ElementRef;

  @ViewChild('formQuestions') formQuestions!: ElementRef;

  @ViewChild('formResult') formResult!: ElementRef;

  @Input() formId!: string;

  get isMobileOrTablet$(): Observable<boolean> {
    return this.breakpointsService.isMobileOrTablet$;
  }

  formHeight$!: Observable<string>;
  resultTop$!: Observable<string>;
  resultToDisplay$ = new BehaviorSubject<string | null>(null);
  currentQuestion$ = new BehaviorSubject<Question | null>(null);
  lastAction$ = new BehaviorSubject<'return' | 'next' | null>(null);
  questionValues!: string[];
  currentQuestion!: Question;
  answers: string[] = [];
  questionId: QuestionValues = QuestionValues.Accompagnement;

  readonly QuestionValues = QuestionValues;
  readonly graph = GRAPH;

  readonly cardConfigPeAdvisor: CejCardResultConfig = {
    title: `Vous pouvez bénéficier d’informations sur le Contrat d’Engagement Jeune auprès de votre conseiller Pôle Emploi`,
    button: {
      text: `Je contacte mon conseiller`,
      type: CejCardResultLinkType.HREF,
      link: 'https://candidat.pole-emploi.fr/espacepersonnel/ ',
      ariaLabel: 'Naviguez vers votre espace personnel sur le site de Pôle Emploi',
    },
  };

  readonly cardConfigPeInscription: CejCardResultConfig = {
    title: `Vous pouvez bénéficier des services de Pôle Emploi`,
    textContent: `Inscrivez-vous à Pôle Emploi pour bénéficier d’un accompagnement répondant à vos besoins`,
    button: {
      text: `Inscrivez-vous à Pôle Emploi`,
      type: CejCardResultLinkType.HREF,
      link: 'https://candidat.pole-emploi.fr/inscription-en-ligne/ma-situation',
      ariaLabel: `Naviguez vers l'inscription en ligne sur le site de Pôle Emploi`,
    },
  };

  private readonly trackEvent = trackEventWithAtInternet;
  private readonly TRACKER_ACTION_CLOSE_CROSS = 'Click Croix';
  private readonly confirmationIds = [
    QuestionValues.OneJOneS,
    QuestionValues.LocalMission,
    QuestionValues.PeSignUp,
    QuestionValues.PeContact,
  ];

  constructor(
    private readonly breakpointsService: BreakpointsService,
    private readonly scrollToService: ScrollToService
  ) {}

  ngOnInit(): void {
    this.questionValues = this.graph.nodes();
    this.setCurrentQuestion();

    this.resultTop$ = combineLatest([this.resultToDisplay$, this.isMobileOrTablet$]).pipe(
      delay(20),
      map(([isMobileOrTablet, resultToDisplay]) =>
        isMobileOrTablet && resultToDisplay ? `${this.getDescriptionHeightWithMargin()}px` : ''
      )
    );

    this.formHeight$ = combineLatest([this.lastAction$, this.isMobileOrTablet$]).pipe(
      withLatestFrom(this.currentQuestion$),
      delay(20),
      map(([[lastAction, isMobileOrTablet], currentQuestion]) => {
        if (isMobileOrTablet) {
          const top = this.getDescriptionHeightWithMargin();
          if (this.resultToDisplay$.value) {
            const formResultHeight = this.formResult.nativeElement.firstElementChild?.clientHeight || 0;
            return `${top + formResultHeight}px`;
          }

          const takeFirstChild = currentQuestion?.value === QuestionValues.Accompagnement || lastAction === 'return';
          const formQuestionstHeight =
            this.formQuestions.nativeElement.children[takeFirstChild ? 0 : 1]?.clientHeight || 0;
          return `${top + formQuestionstHeight}px`;
        }
        return '';
      })
    );
  }

  private getDescriptionHeightWithMargin(): number {
    const formDescriptionRect: DOMRect = this.formDescription.nativeElement.getBoundingClientRect();
    return formDescriptionRect.height + 50;
  }

  answerClicked(answer: Answer, questionId: QuestionValues): void {
    this.trackEvent({ category: 'CEJ', ...answer.trackEvent });
    this.setNextQuestion(questionId, answer.value);
  }

  answersSubmitted(answers: string[]): void {
    this.lastAction$.next('next');
    if (answers.length > 0) {
      this.resultToDisplay$.next(QuestionValues.LocalMission);
    } else {
      this.resultToDisplay$.next(QuestionValues.PeSignUp);
    }
  }

  resetForm(): void {
    this.questionId = QuestionValues.Accompagnement;
    this.answers = [];
    this.setCurrentQuestion();
    this.lastAction$.next('return');
    this.resultToDisplay$.next(null);

    this.isMobileOrTablet$
      .pipe(
        take(1),
        filter((isMobileOrTablet) => isMobileOrTablet),
        delay(10)
      )
      .subscribe({
        next: () => this.scrollToService.scrollTo({ target: '#orientation-form-scroll', offset: -200 }),
      });
  }

  private setNextQuestion(questionId: QuestionValues, answer: string): void {
    this.answers.push(answer);
    this.questionId = this.graph.opposite(questionId, answer) as QuestionValues;
    this.setCurrentQuestion();
    this.lastAction$.next('next');
    if (this.confirmationIds.includes(this.questionId)) {
      this.resultToDisplay$.next(this.questionId);
    }
  }

  trackByString(index: number, str: string): string {
    return `${str}-${index}`;
  }

  trackResult(action: string): void {
    this.trackEvent({ category: 'CEJ', action, type: 'action' });
  }

  closeResult(label: string): void {
    this.trackCLoseAction(label);
    this.resetForm();
  }

  private trackCLoseAction(label: string): void {
    this.trackEvent({
      category: 'CEJ',
      label,
      type: 'action',
      action: this.TRACKER_ACTION_CLOSE_CROSS,
    });
  }

  returnClicked(trackEventLabel?: string): void {
    trackEventLabel &&
      this.trackEvent({ category: 'CEJ', label: trackEventLabel, type: 'action', action: 'Click Retour' });
    this.questionId = this.graph.opposite(this.questionId, this.answers.pop()) as QuestionValues;
    this.setCurrentQuestion();
    this.lastAction$.next('return');
  }

  private setCurrentQuestion(): void {
    const currentQuestion = this.graph.getNodeAttributes(this.questionId);
    if (this.isQuestion(currentQuestion)) {
      this.currentQuestion$.next(currentQuestion);
    }
  }

  private isQuestion(question: Question | { [name: string]: undefined }): question is Question {
    return (question && question.value && !!question.answers) === true;
  }
}

@NgModule({
  imports: [
    CommonModule,
    CejSimpleChoiceQuestionModule,
    CejMultipleChoicesQuestionModule,
    CejOrientationSolutionsModule,
    CejV2FormContactModalModule,
    CejResultCardModule,
  ],
  declarations: [CejOrientationFormComponent],
  exports: [CejOrientationFormComponent],
})
export class CejOrientationFormModule {}
